import Phaser from 'phaser';
import type { Socket } from 'socket.io-client';
import {
  ASSET_PATHS,
  PLAYER_SPRITE,
  SocketEvents,
  SPRITE_KEYS,
  TILE_SIZE_PX,
  type BattleEndPayload,
  type BattleHealedPayload,
  type BattleStartPayload,
  type EncounterStartPayload,
  type ErrorPayload,
  type PartyMemberPayload,
  type PlayerJoinedPayload,
  type PlayerLeftPayload,
  type PlayerMovedPayload,
  type PvpChallengePayload,
  type WorldStatePayload,
} from '@pokemiu/shared';

type Direction = 'up' | 'down' | 'left' | 'right';

interface RemotePlayer {
  sprite: Phaser.GameObjects.Sprite;
  label: Phaser.GameObjects.Text;
  username: string;
  /** Active position tween — kill on snap/reconcile so we don't fight it. */
  walkTween: Phaser.Tweens.Tween | null;
  /** Alternates left/right foot every step so walking reads as walking. */
  stepUseLeftFoot: boolean;
  /** Authoritative tile coords (kept in sync with PLAYER_MOVED). Used for
   * client-side battle-zone pre-check on PvP challenge clicks. */
  tilePos: { x: number; y: number };
}

const MOVE_THROTTLE_MS = 200;
const TWEEN_DURATION_MS = 200;

/**
 * The single overworld scene for M3. Renders the tilemap, the local player,
 * and remote-player sprites. Handles input + client-side prediction +
 * server-authoritative reconcile.
 *
 * Per-player visual differentiation: the same `boy_1` sprite tinted by a
 * deterministic hash of `playerId`. Local player is left untinted (white) so
 * the player can identify themselves at a glance.
 */
export class OverworldScene extends Phaser.Scene {
  private socket!: Socket;
  private localPlayerId!: string;
  private localUsername!: string;
  private localContainer!: Phaser.GameObjects.Container;
  private localSprite!: Phaser.GameObjects.Sprite;
  private localLabel!: Phaser.GameObjects.Text;
  private localWalkTween: Phaser.Tweens.Tween | null = null;
  private localStepUseLeftFoot = true;
  private localTilePos = { x: 8, y: 8 };
  private remotePlayers = new Map<string, RemotePlayer>();
  private lastMoveSentAt = 0;
  private terrainLayer!: Phaser.Tilemaps.TilemapLayer;
  private currentParty: ReadonlyArray<PartyMemberPayload> = [];
  private pokedexContainer!: Phaser.GameObjects.Container;
  private pokedexOpen = false;
  private inBattle = false;
  /** Hovering a remote player sprite shows a single shared tooltip. */
  private hoverTooltip: Phaser.GameObjects.Text | null = null;
  private hoveredPlayerId: string | null = null;
  /** Transient mid-screen toast (e.g. "Move to the arena to challenge"). */
  private infoToast: Phaser.GameObjects.Text | null = null;
  // Reconnect overlay (M5.6)
  private reconnectOverlay: Phaser.GameObjects.GameObject[] = [];
  private reconnectStatus: Phaser.GameObjects.Text | null = null;

  constructor() {
    super({ key: 'OverworldScene' });
  }

  create(): void {
    this.socket = this.game.registry.get('socket') as Socket;
    const tokenPayload = this.game.registry.get('jwtPayload') as
      | { sub: string; username: string }
      | undefined;
    if (!tokenPayload) {
      console.error('[OverworldScene] no jwtPayload in registry — cannot identify local player');
      return;
    }
    this.localPlayerId = tokenPayload.sub;
    this.localUsername = tokenPayload.username;

    this.buildTilemap();
    this.buildAnims();
    this.buildLocalSprite();
    this.buildPokedex();
    this.bindInput();
    this.wireSocketEvents();
  }

  // -------------------- Animations --------------------

  /**
   * Six 2-frame step animations — one per (direction × foot-parity). Each
   * cycles step1→step2 (or step2→step1) over exactly TWEEN_DURATION_MS so
   * the foot pose visibly changes mid-tile-glide. Holding a single static
   * pose for the whole tween reads as moonwalk; cycling reads as walking.
   *
   * `_a` = lead with step1 (one foot pose), `_b` = lead with step2. Caller
   * alternates per move so the same foot doesn't always plant first.
   */
  private buildAnims(): void {
    if (this.anims.exists('walk_down_a')) return; // anims persist across scenes
    const f = PLAYER_SPRITE.frames;
    const D = TWEEN_DURATION_MS;
    const define = (key: string, frames: number[]) => {
      this.anims.create({
        key,
        frames: this.anims.generateFrameNumbers(SPRITE_KEYS.player, { frames }),
        duration: D,
        repeat: 0,
      });
    };
    define('walk_down_a', [f.downStep1, f.downStep2]);
    define('walk_down_b', [f.downStep2, f.downStep1]);
    define('walk_up_a', [f.upStep1, f.upStep2]);
    define('walk_up_b', [f.upStep2, f.upStep1]);
    define('walk_horiz_a', [f.rightStep1, f.rightStep2]);
    define('walk_horiz_b', [f.rightStep2, f.rightStep1]);
  }

  // -------------------- Tilemap --------------------

  private buildTilemap(): void {
    const map = this.make.tilemap({ key: SPRITE_KEYS.maps.grassZone1 });
    const tileset = map.addTilesetImage('general', SPRITE_KEYS.tilesets.general);
    if (!tileset) {
      console.error('[OverworldScene] addTilesetImage returned null — check tileset name');
      return;
    }
    const layer = map.createLayer('terrain', tileset, 0, 0);
    if (!layer) {
      console.error('[OverworldScene] createLayer returned null — terrain layer missing');
      return;
    }
    // Phaser 4 may return a GPU layer; we always create CPU here, so narrow.
    this.terrainLayer = layer as Phaser.Tilemaps.TilemapLayer;
    this.cameras.main.setBounds(0, 0, map.widthInPixels, map.heightInPixels);
  }

  /**
   * Client-side walkability check. Mirrors the server's MapLoader and uses
   * the same `walkable` tile property baked into the .tmj. The server stays
   * authoritative — this lookup just avoids the predict/reconcile jitter when
   * the player walks into a wall.
   */
  private isWalkable(x: number, y: number): boolean {
    const tile = this.terrainLayer.getTileAt(x, y);
    if (!tile) return false;
    return tile.properties.walkable === true;
  }

  /** Mirror of MapLoader.isBattleZone — gates the click-to-challenge UX. */
  private isBattleZoneTile(x: number, y: number): boolean {
    const tile = this.terrainLayer.getTileAt(x, y);
    if (!tile) return false;
    return tile.properties.battleZone === true;
  }

  // -------------------- Local sprite --------------------

  /**
   * Build a container holding the local sprite + username label. Tweening /
   * snapping move the whole container as one unit so the label stays glued
   * to the sprite. Children use container-local coordinates.
   */
  private buildLocalSprite(): void {
    const px = this.tileToPx(this.localTilePos.x);
    const py = this.tileToPx(this.localTilePos.y);

    this.localSprite = this.add.sprite(0, 0, SPRITE_KEYS.player, PLAYER_SPRITE.frames.downIdle);
    this.localSprite.setOrigin(0.5, 0.75);

    this.localLabel = this.makeLabel(this.localUsername, '#ffe066');
    this.localLabel.setPosition(0, -28);

    this.localContainer = this.add.container(px, py, [this.localSprite, this.localLabel]);
    this.cameras.main.startFollow(this.localContainer, true, 0.15, 0.15);
  }

  private buildRemoteSprite(
    playerId: string,
    username: string,
    x: number,
    y: number,
  ): RemotePlayer {
    const px = this.tileToPx(x);
    const py = this.tileToPx(y);
    const sprite = this.add.sprite(px, py, SPRITE_KEYS.player, PLAYER_SPRITE.frames.downIdle);
    sprite.setOrigin(0.5, 0.75);
    sprite.setTint(this.tintFor(playerId));
    sprite.setInteractive({ useHandCursor: true });
    sprite.on('pointerover', () => this.showHoverTooltip(playerId));
    sprite.on('pointerout', () => this.hideHoverTooltip());
    sprite.on('pointerdown', () => this.attemptChallenge(playerId));
    const label = this.makeLabel(username, '#a0d8ff');
    label.setPosition(px, py - 20);
    const rp: RemotePlayer = {
      sprite,
      label,
      username,
      walkTween: null,
      stepUseLeftFoot: true,
      tilePos: { x, y },
    };
    this.remotePlayers.set(playerId, rp);
    return rp;
  }

  private makeLabel(text: string, color: string): Phaser.GameObjects.Text {
    return this.add
      .text(0, 0, text, {
        fontFamily: 'monospace',
        fontSize: '8px',
        color,
        stroke: '#000',
        strokeThickness: 2,
      })
      .setOrigin(0.5, 1);
  }

  /** Deterministic 24-bit colour from a playerId string. */
  private tintFor(playerId: string): number {
    let h = 0;
    for (let i = 0; i < playerId.length; i++) {
      h = (h * 31 + playerId.charCodeAt(i)) | 0;
    }
    // Force a not-too-dark hue so tinted sprites stay legible.
    const r = 0x80 | (h & 0x7f);
    const g = 0x80 | ((h >> 7) & 0x7f);
    const b = 0x80 | ((h >> 14) & 0x7f);
    return (r << 16) | (g << 8) | b;
  }

  // -------------------- Input --------------------

  private bindInput(): void {
    // Listen on window (not on Phaser's keyboard plugin) so that synthetic
    // keydown events (e.g. from automated tests) and clicks-anywhere-on-page
    // are picked up. Phaser's keyboard plugin uses internal capture which
    // tends to require canvas focus and ignores dispatched KeyboardEvents.
    const handle = (dir: Direction) => {
      if (this.pokedexOpen || this.inBattle) return; // suspended during these
      const now = this.time.now;
      if (now - this.lastMoveSentAt < MOVE_THROTTLE_MS) return;
      this.lastMoveSentAt = now;
      this.predictAndSend(dir);
    };
    const onKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          handle('up');
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          handle('down');
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          handle('left');
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          handle('right');
          break;
        case 'p':
        case 'P':
          if (this.inBattle) return; // BattleScene owns the input while open
          this.togglePokedex();
          break;
        case 'Escape':
          if (this.pokedexOpen) this.togglePokedex();
          break;
        default:
          return;
      }
    };
    window.addEventListener('keydown', onKey);
    // Detach on scene shutdown so reload / scene-restart doesn't leak handlers.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener('keydown', onKey);
    });
  }

  override update(): void {}

  private predictAndSend(dir: Direction): void {
    // Always face the direction the player is trying to move — gives feedback
    // even when blocked.
    this.applyDirectionFrame(this.localSprite, dir);

    const delta = this.deltaFor(dir);
    const next = { x: this.localTilePos.x + delta.x, y: this.localTilePos.y + delta.y };

    // Bail before predicting if we already know the destination is blocked.
    // Avoids the jump-into-wall-then-snap-back jitter; server stays
    // authoritative for the actual walkability decision.
    if (!this.isWalkable(next.x, next.y)) return;

    this.localTilePos = next;
    const px = this.tileToPx(next.x);
    const py = this.tileToPx(next.y);

    // Cycle step1→step2 (alternating which foot leads per move) for the
    // whole tween so the feet visibly progress while the body glides. A
    // static pose held for the whole tween reads as moonwalk.
    this.localWalkTween?.stop();
    this.playStepAnim(this.localSprite, dir, this.localStepUseLeftFoot);
    this.localStepUseLeftFoot = !this.localStepUseLeftFoot;
    this.localWalkTween = this.tweens.add({
      targets: this.localContainer,
      x: px,
      y: py,
      duration: TWEEN_DURATION_MS,
      onComplete: () => {
        this.localSprite.stop();
        this.applyDirectionFrame(this.localSprite, dir);
        this.localWalkTween = null;
      },
    });

    this.socket.emit(SocketEvents.PLAYER_MOVE, { direction: dir });
  }

  /**
   * Play the matching 2-frame step anim for `dir`. `useLeadStep1` picks
   * which foot plants first; caller flips it per move so consecutive steps
   * alternate the lead foot.
   */
  private playStepAnim(
    sprite: Phaser.GameObjects.Sprite,
    dir: Direction,
    useLeadStep1: boolean,
  ): void {
    const suffix = useLeadStep1 ? 'a' : 'b';
    switch (dir) {
      case 'up':
        sprite.setFlipX(false);
        sprite.play(`walk_up_${suffix}`);
        break;
      case 'down':
        sprite.setFlipX(false);
        sprite.play(`walk_down_${suffix}`);
        break;
      case 'right':
        sprite.setFlipX(false);
        sprite.play(`walk_horiz_${suffix}`);
        break;
      case 'left':
        sprite.setFlipX(true);
        sprite.play(`walk_horiz_${suffix}`);
        break;
    }
  }

  /** Derive the walk direction from the position delta of a remote player. */
  private dirFromDelta(dx: number, dy: number): Direction | null {
    if (dx === 0 && dy === 0) return null;
    if (Math.abs(dx) >= Math.abs(dy)) return dx > 0 ? 'right' : 'left';
    return dy > 0 ? 'down' : 'up';
  }

  private deltaFor(dir: Direction): { x: number; y: number } {
    switch (dir) {
      case 'up':
        return { x: 0, y: -1 };
      case 'down':
        return { x: 0, y: 1 };
      case 'left':
        return { x: -1, y: 0 };
      case 'right':
        return { x: 1, y: 0 };
    }
  }

  private applyDirectionFrame(sprite: Phaser.GameObjects.Sprite, dir: Direction): void {
    const f = PLAYER_SPRITE.frames;
    switch (dir) {
      case 'up':
        sprite.setFrame(f.upIdle);
        sprite.setFlipX(false);
        break;
      case 'down':
        sprite.setFrame(f.downIdle);
        sprite.setFlipX(false);
        break;
      case 'right':
        sprite.setFrame(f.rightIdle);
        sprite.setFlipX(false);
        break;
      case 'left':
        sprite.setFrame(f.rightIdle);
        sprite.setFlipX(true);
        break;
    }
  }

  // -------------------- Socket events --------------------

  private wireSocketEvents(): void {
    this.socket.on(SocketEvents.WORLD_STATE, (p: WorldStatePayload) => {
      // Snap local container to authoritative position (drift / reconnect).
      this.localWalkTween?.stop();
      this.localWalkTween = null;
      this.localTilePos = { x: p.self.position.x, y: p.self.position.y };
      const px = this.tileToPx(this.localTilePos.x);
      const py = this.tileToPx(this.localTilePos.y);
      this.localContainer.setPosition(px, py);
      this.currentParty = p.self.party;
      if (this.pokedexOpen) this.refreshPokedex();
      for (const entry of p.players) {
        if (entry.playerId === this.localPlayerId) continue;
        if (this.remotePlayers.has(entry.playerId)) continue;
        this.buildRemoteSprite(entry.playerId, entry.username, entry.position.x, entry.position.y);
      }
    });

    this.socket.on(SocketEvents.PLAYER_JOINED, (p: PlayerJoinedPayload) => {
      if (p.playerId === this.localPlayerId) return;
      if (this.remotePlayers.has(p.playerId)) return;
      this.buildRemoteSprite(p.playerId, p.username, p.position.x, p.position.y);
    });

    this.socket.on(SocketEvents.PLAYER_LEFT, (p: PlayerLeftPayload) => {
      const rp = this.remotePlayers.get(p.playerId);
      if (!rp) return;
      if (this.hoveredPlayerId === p.playerId) this.hideHoverTooltip();
      rp.sprite.destroy();
      rp.label.destroy();
      this.remotePlayers.delete(p.playerId);
    });

    this.socket.on(SocketEvents.PLAYER_MOVED, (p: PlayerMovedPayload) => {
      if (p.playerId === this.localPlayerId) {
        // Self echo: snap back if the server says we mispredicted.
        if (p.reconcile) {
          this.localWalkTween?.stop();
          this.localWalkTween = null;
          this.localTilePos = { x: p.position.x, y: p.position.y };
          const px = this.tileToPx(p.position.x);
          const py = this.tileToPx(p.position.y);
          this.localContainer.setPosition(px, py);
          this.applyDirectionFrame(this.localSprite, 'down');
        }
        return;
      }
      // Remote player: derive direction from delta, cycle step anim during
      // the tween (alternating which foot leads), drop back to idle on end.
      const rp = this.remotePlayers.get(p.playerId);
      if (!rp) return;
      rp.tilePos = { x: p.position.x, y: p.position.y };
      const px = this.tileToPx(p.position.x);
      const py = this.tileToPx(p.position.y);
      const dir = this.dirFromDelta(px - rp.sprite.x, py - rp.sprite.y);
      if (dir) {
        this.playStepAnim(rp.sprite, dir, rp.stepUseLeftFoot);
        rp.stepUseLeftFoot = !rp.stepUseLeftFoot;
      }
      rp.walkTween?.stop();
      rp.walkTween = this.tweens.add({
        targets: [rp.sprite, rp.label],
        x: px,
        y: (target: { y: number }) => (target === rp.label ? py - 20 : py),
        duration: TWEEN_DURATION_MS,
        onComplete: () => {
          rp.sprite.stop();
          if (dir) this.applyDirectionFrame(rp.sprite, dir);
          rp.walkTween = null;
        },
      });
    });

    this.socket.on(SocketEvents.BATTLE_HEALED, (p: BattleHealedPayload) => {
      this.currentParty = p.party;
      if (this.pokedexOpen) this.refreshPokedex();
      this.playHealEffect();
    });

    this.socket.on(SocketEvents.ENCOUNTER_START, (p: EncounterStartPayload) => {
      this.inBattle = true;
      this.localWalkTween?.stop();
      this.localWalkTween = null;
      this.applyDirectionFrame(this.localSprite, 'down');
      this.scene.launch('BattleScene', { encounter: p });
    });

    this.socket.on(SocketEvents.BATTLE_START, (p: BattleStartPayload) => {
      // PvP battle initiated by an opponent's pvp:challenge or by our own.
      this.inBattle = true;
      this.localWalkTween?.stop();
      this.localWalkTween = null;
      this.applyDirectionFrame(this.localSprite, 'down');
      this.scene.launch('BattleScene', { start: p });
    });

    this.socket.on(SocketEvents.BATTLE_END, (p: BattleEndPayload) => {
      // BattleScene handles the user-facing transition; we just refresh
      // party cache so the Pokedex reflects post-battle HP.
      this.currentParty = p.party;
      if (this.pokedexOpen) this.refreshPokedex();
    });

    this.socket.on(SocketEvents.SESSION_TERMINATED, () => {
      // The auth gate (main.ts) handles the user-visible side; we just stop
      // animating to avoid noise.
      this.scene.pause();
    });

    this.socket.on(SocketEvents.ERROR, (err: ErrorPayload) => {
      console.warn('[OverworldScene] server error:', err.code, err.message);
    });

    // ---- Reconnect overlay wiring (M5.6) ----
    this.socket.on('disconnect', (reason: string) => {
      // 'io client disconnect' = user-initiated; never overlay (logout flow).
      if (reason === 'io client disconnect') return;
      this.openReconnectOverlay('Reconnecting...');
    });
    this.socket.io.on('reconnect_attempt', (attempt: number) => {
      this.updateReconnectOverlay(`Reconnecting... (try ${attempt})`);
    });
    this.socket.on('connect', () => {
      this.closeReconnectOverlay();
      // Server resends world:state automatically as part of handleConnect; no
      // explicit re-request needed but doesn't hurt as a belt-and-braces.
      this.socket.emit(SocketEvents.WORLD_REQUEST);
    });
    this.socket.io.on('reconnect_failed', () => {
      this.openReconnectOverlay('Connection lost. Please reload.', /*final*/ true);
    });

    // Resync: the initial world:state may have arrived during BootScene's
    // preload phase before our listener was bound. Ask the server for a fresh
    // snapshot now that we're ready.
    this.socket.emit(SocketEvents.WORLD_REQUEST);
  }

  // -------------------- Reconnect overlay --------------------

  private openReconnectOverlay(text: string, final = false): void {
    this.closeReconnectOverlay();
    const W = this.cameras.main.width;
    const H = this.cameras.main.height;
    const dim = this.add
      .rectangle(0, 0, W, H, 0x000000, 0.7)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(900);
    const status = this.add
      .text(W / 2, H / 2, text, {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: final ? '#ff8866' : '#ffe066',
        align: 'center',
      })
      .setOrigin(0.5, 0.5)
      .setScrollFactor(0)
      .setDepth(901);
    this.reconnectOverlay = [dim, status];
    this.reconnectStatus = status;
  }

  private updateReconnectOverlay(text: string): void {
    if (!this.reconnectStatus) {
      this.openReconnectOverlay(text);
      return;
    }
    this.reconnectStatus.setText(text);
  }

  private closeReconnectOverlay(): void {
    for (const obj of this.reconnectOverlay) obj.destroy();
    this.reconnectOverlay = [];
    this.reconnectStatus = null;
  }

  /**
   * Called by BattleScene right before it stops itself. Re-enables movement
   * input. Party cache was already updated in the BATTLE_END handler.
   */
  exitBattle(): void {
    this.inBattle = false;
  }

  // -------------------- Helpers --------------------

  private tileToPx(tile: number): number {
    return tile * TILE_SIZE_PX + TILE_SIZE_PX / 2;
  }

  /**
   * Brief yellow tint on the sprite + scale pulse on the whole container
   * (sprite + HP bar + label all bounce together) to mark the moment the
   * server confirmed a full-party heal. Cheap visual feedback only —
   * authoritative HP values arrive in the BattleHealedPayload.party array.
   */
  private playHealEffect(): void {
    this.localSprite.setTint(0xfff066);
    this.tweens.add({
      targets: this.localContainer,
      scale: { from: 1, to: 1.25 },
      duration: 180,
      yoyo: true,
      onComplete: () => {
        this.localSprite.clearTint();
        this.localContainer.setScale(1);
      },
    });
  }

  // -------------------- PvP challenge UX --------------------

  /**
   * Show a small label above the hovered remote sprite. Static "Challenge!"
   * — we don't try to reflect the zone-eligibility check here since the
   * server is the authority and the player can move into the zone before
   * clicking. The actual gating happens in `attemptChallenge`.
   */
  private showHoverTooltip(playerId: string): void {
    const rp = this.remotePlayers.get(playerId);
    if (!rp) return;
    this.hoveredPlayerId = playerId;
    if (!this.hoverTooltip) {
      this.hoverTooltip = this.add
        .text(0, 0, '', {
          fontFamily: 'monospace',
          fontSize: '8px',
          color: '#ffd966',
          backgroundColor: '#000000',
          padding: { left: 3, right: 3, top: 1, bottom: 1 },
        })
        .setOrigin(0.5, 1)
        .setDepth(900);
    }
    this.hoverTooltip.setText(`Challenge ${rp.username}!`);
    this.hoverTooltip.setPosition(rp.sprite.x, rp.sprite.y - 32);
    this.hoverTooltip.setVisible(true);
  }

  private hideHoverTooltip(): void {
    this.hoveredPlayerId = null;
    this.hoverTooltip?.setVisible(false);
  }

  /**
   * Click handler for remote sprites. Pre-checks both players are in the
   * battle zone (and we're not already in a battle); on success, emits
   * `pvp:challenge`. Server re-validates per FR-012 AC2.
   */
  private attemptChallenge(targetPlayerId: string): void {
    if (this.inBattle) {
      this.showInfoToast('Already in a battle');
      return;
    }
    const rp = this.remotePlayers.get(targetPlayerId);
    if (!rp) return;
    const localInZone = this.isBattleZoneTile(this.localTilePos.x, this.localTilePos.y);
    const remoteInZone = this.isBattleZoneTile(rp.tilePos.x, rp.tilePos.y);
    if (!localInZone || !remoteInZone) {
      this.showInfoToast('Move to the arena to challenge');
      return;
    }
    const payload: PvpChallengePayload = { targetPlayerId };
    this.socket.emit(SocketEvents.PVP_CHALLENGE, payload);
  }

  /** Brief mid-screen text that fades after 1.5s. Reuses a single object. */
  private showInfoToast(text: string): void {
    if (!this.infoToast) {
      this.infoToast = this.add
        .text(0, 0, '', {
          fontFamily: 'monospace',
          fontSize: '10px',
          color: '#ffffff',
          backgroundColor: '#000000aa',
          padding: { left: 6, right: 6, top: 3, bottom: 3 },
        })
        .setOrigin(0.5, 0.5)
        .setScrollFactor(0)
        .setDepth(950);
    }
    const cam = this.cameras.main;
    this.infoToast.setText(text);
    this.infoToast.setPosition(cam.width / 2, cam.height - 28);
    this.infoToast.setAlpha(1);
    this.infoToast.setVisible(true);
    this.tweens.killTweensOf(this.infoToast);
    this.tweens.add({
      targets: this.infoToast,
      alpha: 0,
      duration: 350,
      delay: 1200,
      onComplete: () => this.infoToast?.setVisible(false),
    });
  }

  // -------------------- Pokedex overlay --------------------

  /**
   * Build the screen-fixed pokedex container once (hidden by default). The
   * container is repopulated on every `togglePokedex` open so it always
   * reflects `currentParty` at the moment the player presses P. Sprites
   * inside the container reuse the front-sprite textures already loaded by
   * BootScene — no extra preload needed.
   */
  private buildPokedex(): void {
    this.pokedexContainer = this.add.container(0, 0);
    this.pokedexContainer.setScrollFactor(0); // screen-fixed
    this.pokedexContainer.setDepth(1000);
    this.pokedexContainer.setVisible(false);
  }

  private togglePokedex(): void {
    this.pokedexOpen = !this.pokedexOpen;
    if (this.pokedexOpen) {
      this.refreshPokedex();
    }
    this.pokedexContainer.setVisible(this.pokedexOpen);
  }

  /** Re-build pokedex content from `currentParty`. Cheap — at most 6 rows. */
  private refreshPokedex(): void {
    this.pokedexContainer.removeAll(true);
    const W = this.cameras.main.width;
    const H = this.cameras.main.height;

    const bg = this.add.rectangle(0, 0, W, H, 0x000000, 0.85).setOrigin(0, 0);
    const title = this.add
      .text(W / 2, 8, 'PARTY  (P / Esc to close)', {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#ffe066',
      })
      .setOrigin(0.5, 0);
    this.pokedexContainer.add([bg, title]);

    if (this.currentParty.length === 0) {
      const empty = this.add
        .text(W / 2, H / 2, 'No Pokémon yet.', {
          fontFamily: 'monospace',
          fontSize: '10px',
          color: '#aaaaaa',
        })
        .setOrigin(0.5, 0.5);
      this.pokedexContainer.add(empty);
      return;
    }

    // 2 columns × 3 rows grid. Slot 150×64 inside 320×240 canvas.
    const SLOT_W = 150;
    const SLOT_H = 64;
    const GRID_TOP = 26;
    for (let i = 0; i < this.currentParty.length && i < 6; i++) {
      const member = this.currentParty[i]!;
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = 10 + col * (SLOT_W + 0);
      const y = GRID_TOP + row * (SLOT_H + 4);
      this.pokedexContainer.add(this.buildPokedexRow(member, x, y, SLOT_W, SLOT_H));
    }
  }

  private buildPokedexRow(
    member: PartyMemberPayload,
    x: number,
    y: number,
    w: number,
    h: number,
  ): Phaser.GameObjects.GameObject[] {
    const frame = this.add.rectangle(x, y, w, h, 0x222244, 0.9).setOrigin(0, 0);
    const spriteKey = SPRITE_KEYS.creatures.front(member.speciesId);
    const hasTexture = this.textures.exists(spriteKey);
    const objs: Phaser.GameObjects.GameObject[] = [frame];
    if (hasTexture) {
      const sprite = this.add.image(x + 6, y + h / 2, spriteKey);
      sprite.setScale(0.5); // 64x64 source → 32x32 rendered
      sprite.setOrigin(0, 0.5);
      if (member.fainted) sprite.setAlpha(0.4);
      objs.push(sprite);
    } else {
      // Defensive: if BootScene didn't preload this species, log + show a
      // placeholder rectangle so the slot still renders something.
      console.warn(
        `[Pokedex] missing texture for ${member.speciesId}; expected ${ASSET_PATHS.creatureFront(
          member.speciesId,
        )}`,
      );
      const ph = this.add.rectangle(x + 6, y + h / 2, 32, 32, 0x444444).setOrigin(0, 0.5);
      objs.push(ph);
    }

    // Vertical layout inside a 64px-tall slot:
    //   y+ 2  name (10px)
    //   y+14  Lv.X (8px) — right side
    //   y+24  HP cur/max (8px)
    //   y+34  HP bar (3px)
    //   y+40  XP into/span (7px)
    //   y+50  XP bar (3px)
    const textColor = member.fainted ? '#aa6666' : '#ffffff';
    const barX = x + 42;
    const barW = w - 50;

    const name = this.add.text(x + 42, y + 2, member.speciesDisplayName, {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: textColor,
    });
    const lvl = this.add.text(x + w - 6, y + 4, `Lv.${member.level}`, {
      fontFamily: 'monospace',
      fontSize: '8px',
      color: textColor,
    });
    lvl.setOrigin(1, 0);

    const hpColor = this.hpBarColor(member.currentHp, member.maxHp, member.fainted);
    const hp = this.add.text(x + 42, y + 22, `HP ${member.currentHp}/${member.maxHp}`, {
      fontFamily: 'monospace',
      fontSize: '8px',
      color: hpColor,
    });
    const hpRatio = Math.max(0, Math.min(1, member.currentHp / member.maxHp));
    const hpBg = this.add.rectangle(barX, y + 34, barW, 3, 0x000000, 0.7).setOrigin(0, 0);
    const hpFg = this.add
      .rectangle(
        barX,
        y + 34,
        barW * hpRatio,
        3,
        this.hpBarColorInt(member.currentHp, member.maxHp, member.fainted),
      )
      .setOrigin(0, 0);
    objs.push(name, lvl, hp, hpBg, hpFg);

    // XP bar (M4.2): visual progress to next level. Hidden at level cap
    // (xpForNextLevel = null) or when server hasn't sent the fields yet
    // (defensive — xp* may be undefined on a stale server build).
    if (
      member.xpForNextLevel != null &&
      member.xpForCurrentLevel != null &&
      typeof member.xpTotal === 'number'
    ) {
      const span = Math.max(1, member.xpForNextLevel - member.xpForCurrentLevel);
      const into = Math.max(0, member.xpTotal - member.xpForCurrentLevel);
      const ratio = Math.min(1, into / span);
      const xpLabel = this.add.text(barX, y + 41, `XP ${into}/${span}`, {
        fontFamily: 'monospace',
        fontSize: '7px',
        color: '#7ec8e3',
      });
      const xpBg = this.add.rectangle(barX, y + 52, barW, 3, 0x000000, 0.7).setOrigin(0, 0);
      const xpFg = this.add.rectangle(barX, y + 52, barW * ratio, 3, 0x66ccff).setOrigin(0, 0);
      objs.push(xpLabel, xpBg, xpFg);
    }
    return objs;
  }

  private hpBarColorInt(cur: number, max: number, fainted: boolean): number {
    if (fainted || cur <= 0) return 0xaa6666;
    const pct = cur / max;
    if (pct > 0.5) return 0x66dd66;
    if (pct > 0.2) return 0xffcc33;
    return 0xdd6666;
  }

  private hpBarColor(cur: number, max: number, fainted: boolean): string {
    if (fainted || cur <= 0) return '#aa6666';
    const pct = cur / max;
    if (pct > 0.5) return '#66dd66';
    if (pct > 0.2) return '#ffcc33';
    return '#dd6666';
  }
}
